import { Box, Flex, IconButton, Text, chakra, HStack } from '@chakra-ui/react';
import { PossibleEnrichments } from '@outdoorly/react-chat-sdk';
import React, { useRef, useState } from 'react';
import { LeftChevron, RightChevron } from '../../../icons';
import { ImageWithSrcSet } from '../../ImageWithSrcset/index';
import Carousel from 'nuka-carousel';
import { useMemoizedFrame } from '../../../utils/hooks';
type RecommendationProps = {
  recommendation: PossibleEnrichments['recommendation'];
  isLastMessage: boolean;
};

const ImageSlide = (props: { image: { url: string; srcset?: string | null }; id: string; alt: string }) => {
  const { image, id, alt } = props;

  return (
    <Box key={id} w="100%" h="240px" borderRadius="8px 8px 0 0" background="scheme.backgroundSecondary">
      <ImageWithSrcSet
        src={image.url}
        srcSet={image.srcset}
        alt={alt}
        width="full"
        height="240px"
        objectFit="contain"
        borderRadius="8px 8px 0 0"
        backgroundColor="scheme.backgroundSecondary"
        mixBlendMode="darken"
        sx={{
          WebkitUserSelect: 'none',
          WebkitUserDrag: 'none'
        }}
      />
    </Box>
  );
};
export const Recommendation: React.FC<RecommendationProps> = React.memo((props: RecommendationProps) => {
  const { recommendation, isLastMessage } = props;
  const images = recommendation?.product?.images?.slice(0, 3) || [];
  const shouldShowSwiper = images.length > 1;
  const [focusable, setFocusable] = useState(false);
  const leftButtonRef = useRef<null | HTMLButtonElement>(null);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const { document, window } = useMemoizedFrame();
  return (
    <Box role="row">
      <Box
        w="80%"
        ml="28px"
        role="gridcell"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setFocusable(false);
            containerRef?.current?.focus();
          } else if (e.key === 'Enter' && document?.activeElement === containerRef.current) {
            e.preventDefault();
            setFocusable(true);
            leftButtonRef?.current?.focus();
          }
        }}
        ref={containerRef}
        aria-label="Press enter to view product recommendation details"
      >
        {!shouldShowSwiper ? (
          <Box h="100%" w="100%" background="scheme.backgroundSecondary" borderRadius="8px 8px 0 0" mt="4px">
            <ImageWithSrcSet
              src={images?.[0]?.url}
              srcSet={images?.[0]?.srcset}
              alt={`image of ${recommendation?.product?.name}`}
              width="full"
              height="240px"
              objectFit="contain"
              borderRadius="8px 8px 0 0"
              backgroundColor="scheme.backgroundSecondary"
              mixBlendMode="darken"
              sx={{
                WebkitUserSelect: 'none',
                WebkitUserDrag: 'none'
              }}
            />
          </Box>
        ) : (
          <Box
            h="240px"
            w="100%"
            pos="relative"
            background="scheme.backgroundSecondary"
            borderRadius="8px 8px 0 0"
            mt="4px"
          >
            <Carousel
              autoplay={false}
              dragThreshold={0.3}
              wrapAround
              frameAriaLabel={`${recommendation?.product?.name ?? ''} - ${
                recommendation?.variant?.name ?? ''
              }  images`}
              renderBottomCenterControls={({ currentSlide, slideCount, goToSlide }) => (
                <HStack
                  borderRadius="full"
                  bg="scheme.backgroundPrimary"
                  padding="5px"
                  align="center"
                  spacing="4px"
                  borderColor="scheme.borderPrimary"
                  borderWidth="1px"
                >
                  {[...Array(slideCount)].map((_, index) => (
                    <chakra.button
                      onClick={() => goToSlide(index)}
                      type="button"
                      aria-label={`slide ${index + 1} bullet`}
                      cursor="pointer"
                      backgroundColor={currentSlide === index ? 'scheme.whiteCompatible' : 'scheme.neutral3'}
                      height="6px"
                      width="6px"
                      borderRadius="full"
                      key={index}
                      tabIndex={-1}
                    />
                  ))}
                </HStack>
              )}
              renderCenterLeftControls={({ previousSlide }) => (
                <IconButton
                  onClick={previousSlide}
                  aria-label="previous photo"
                  w="20px"
                  h="20px"
                  minW="20px"
                  border="0.5px solid"
                  marginLeft="12px"
                  zIndex="docked"
                  borderColor="scheme.borderPrimary"
                  borderRadius="8px"
                  boxShadow="0px 2px 4px rgba(0, 0, 0, 0.06)"
                  background="scheme.backgroundPrimary"
                  _hover={{
                    background: 'scheme.backgroundPrimary',
                    borderColor: 'scheme.borderSecondary',
                    '& > svg': {
                      fill: 'scheme.contentSecondary'
                    }
                  }}
                  tabIndex={focusable ? 0 : -1}
                  _active={{
                    background: 'scheme.backgroundPrimary',
                    boxShadow: 'none',
                    '& > svg': {
                      fill: 'scheme.contentPrimary'
                    }
                  }}
                  ref={leftButtonRef}
                  icon={<LeftChevron fill="scheme.contentTertiary" width="6px" />}
                />
              )}
              renderCenterRightControls={({ nextSlide }) => (
                <IconButton
                  onClick={nextSlide}
                  aria-label="next photo"
                  w="20px"
                  h="20px"
                  minW="20px"
                  maxH="20px"
                  marginRight="12px"
                  borderRadius="8px"
                  background="scheme.backgroundPrimary"
                  boxShadow="0px 2px 4px rgba(0, 0, 0, 0.06)"
                  border="0.5px solid"
                  zIndex="docked"
                  borderColor="scheme.borderPrimary"
                  _hover={{
                    background: 'scheme.backgroundPrimary',
                    borderColor: 'scheme.borderSecondary',
                    '& > svg': {
                      fill: 'scheme.contentSecondary'
                    }
                  }}
                  tabIndex={focusable ? 0 : -1}
                  _active={{
                    background: 'scheme.backgroundPrimary',
                    boxShadow: 'none',
                    '& > svg': {
                      fill: 'scheme.contentPrimary'
                    }
                  }}
                  icon={<RightChevron fill="scheme.contentTertiary" width="6px" />}
                />
              )}
            >
              {images.map((image, index) => {
                return (
                  <ImageSlide
                    id={image.id}
                    image={image}
                    key={index}
                    alt={`image of ${recommendation?.product?.name}`}
                  />
                );
              })}
            </Carousel>
          </Box>
        )}
        <Box
          background="scheme.backgroundSecondary"
          p="12px"
          borderRadius="0 0 8px 8px"
          aria-live={isLastMessage ? 'polite' : 'off'}
        >
          <Flex flexDir="column">
            <Text
              as="a"
              variant="label-medium"
              textDecoration="underline"
              cursor="pointer"
              tabIndex={focusable ? 0 : -1}
              color="scheme.contentSecondary"
              href={`${recommendation?.product?.externalUrl} `}
              target="_blank"
            >
              {recommendation?.product?.name ?? ''}
            </Text>
            <Flex mt="4px" align="baseline">
              {recommendation?.product?.price?.price && (
                <Text color="contentPrimary" variant="label-large" mr="4px">
                  {Intl.NumberFormat(window?.navigator.language, {
                    style: 'currency',
                    currency: recommendation?.product?.price?.currency
                  }).format(recommendation?.product?.price?.price)}
                </Text>
              )}
              {recommendation?.variant?.name && (
                <Text color="scheme.contentTertiary" variant="label-small">
                  · {recommendation?.variant?.name}
                </Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
});
